"use client";

import Link from "next/link";
import { useLayoutEffect, useRef, useState } from "react";
import type { LibraryContext } from "@/native-shell/library";
import type { PlannerPreferences } from "@/lib/planner/preferences";
import {
  applyPersonalPreferenceSuggestions,
  type PersonalSuggestionId,
} from "@/lib/planner/personal-preferences";
import { ROUTE_OUTCOME_REASONS } from "@/lib/planner/route-outcomes";
import {
  feedbackEvidenceKey,
  readFeedbackSnapshot,
  reviewOutingFeedback,
  type OutingFeedbackSnapshot,
} from "@/lib/companion/outing-feedback";
import { loadFeedback } from "@/lib/companion/outing-feedback-client";
import { listFeedbackDrafts } from "@/lib/companion/outing-feedback-store";
import {
  feedbackButton as button,
  useFeedbackAction,
} from "@/app/_components/companion/FeedbackAccount";

export function OutingSuggestions({
  context,
  ownerKey,
  prefs,
  busy: plannerBusy,
  temporary,
  onClear,
  onApply,
}: {
  context: LibraryContext;
  ownerKey: string;
  prefs: PlannerPreferences;
  busy: boolean;
  temporary: boolean;
  onClear: () => void;
  onApply: (
    next: PlannerPreferences,
    fields: string[],
    expected: string,
    ownerKey: string,
  ) => void;
}) {
  const [snapshot, setSnapshot] = useState<OutingFeedbackSnapshot | null>(null),
    [selected, setSelected] = useState<PersonalSuggestionId[]>([]);
  const [reviewed, setReviewed] = useState<{
    evidence: string;
    prefs: string;
    selected: PersonalSuggestionId[];
    checkedAt: string;
  } | null>(null);
  const { busy, message, setMessage, run } = useFeedbackAction(context);
  const latest = useRef({ prefs, plannerBusy, ownerKey });
  useLayoutEffect(() => {
    latest.current = { prefs, plannerBusy, ownerKey };
  }, [prefs, plannerBusy, ownerKey]);
  const review = snapshot ? reviewOutingFeedback(snapshot, prefs.sport) : null;
  const preview = review
    ? applyPersonalPreferenceSuggestions(
        prefs,
        review,
        reviewed?.selected ?? selected,
      )
    : null;
  const stale = reviewed && reviewed.prefs !== JSON.stringify(prefs);
  const load = () =>
    void run(
      async () => {
        if ((await listFeedbackDrafts(context)).length)
          throw new Error(
            "Resolve pending feedback changes in You before reviewing suggestions.",
          );
        return loadFeedback(context);
      },
      (data) => {
        setSnapshot(data);
        setSelected([]);
        setReviewed(null);
      },
    );
  return (
    <details className="ph-no-capture rounded-xl border border-border-light bg-white p-4 lg:col-span-2">
      <summary className="flex min-h-11 cursor-pointer items-center text-sm font-semibold text-navy">
        Suggestions from your outings
      </summary>
      <div className="mt-3 space-y-3">
        <p className="text-sm text-text-muted">
          Private answers can suggest preferences for this planner visit.
          Ability, access, effort limits and your finish deadline remain your
          choices.
        </p>
        <Link
          className="inline-flex min-h-11 items-center text-sm font-semibold text-teal underline"
          href="/companion/you/feedback"
        >
          Manage feedback and suggestions
        </Link>
        <div>
          <button
            className={button}
            disabled={busy || plannerBusy}
            onClick={load}
          >
            {snapshot ? "Refresh suggestions" : "Check my suggestions"}
          </button>
        </div>
        {temporary && (
          <div className="rounded-lg bg-teal/10 p-3 text-sm">
            <p>
              Using your reviewed preferences for this planner visit. They are
              not saved as this browser&apos;s defaults. Reloading, changing
              account or clearing them restores those defaults.
            </p>
            <button
              className={button + " mt-2"}
              disabled={busy || plannerBusy}
              onClick={onClear}
            >
              Clear visit preferences
            </button>
          </div>
        )}
        {snapshot && !snapshot.settings.enabled && (
          <p className="text-sm">
            Suggestions are off. You can enable them from your private feedback
            settings.
          </p>
        )}
        {snapshot?.settings.enabled && review && (
          <>
            <p className="text-sm">
              Based on {review.dayCount} separate{" "}
              {prefs.sport === "mtb"
                ? "mountain biking"
                : "skiing and snowboarding"}{" "}
              days in the past year. A suggestion needs three days of feedback
              and its reason on at least two days.
            </p>
            {!review.suggestions.length && (
              <p className="text-sm text-text-muted">
                No repeated preference to suggest yet.
              </p>
            )}
            <fieldset
              disabled={busy || plannerBusy || !!reviewed}
              className="space-y-2"
            >
              <legend className="text-sm font-semibold">
                Choose changes to review
              </legend>
              {review.suggestions.map((s) => {
                const already = !applyPersonalPreferenceSuggestions(
                  prefs,
                  review,
                  [s.id],
                ).changes.length;
                return (
                  <label
                    key={s.id}
                    className="flex min-h-11 gap-3 rounded-lg border border-border p-3 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(s.id) && !already}
                      disabled={already}
                      onChange={(e) =>
                        setSelected(
                          e.target.checked
                            ? [...selected, s.id]
                            : selected.filter((id) => id !== s.id),
                        )
                      }
                    />
                    <span>
                      <strong>{s.title}</strong>
                      <span className="mt-1 block">
                        {s.supportingDays} days:{" "}
                        {
                          ROUTE_OUTCOME_REASONS.find(
                            ([r]) => r === s.reason,
                          )?.[1]
                        }
                        .{" "}
                        {already
                          ? "Already selected in your planner."
                          : s.explanation}
                      </span>
                    </span>
                  </label>
                );
              })}
            </fieldset>
            {reviewed ? (
              <div className="space-y-3 rounded-lg bg-warm-bg p-3">
                <h3 className="text-sm font-semibold">
                  Review changes for this visit
                </h3>
                <ul className="list-disc pl-5 text-sm">
                  {preview?.changes.map((change) => (
                    <li key={change}>{change}</li>
                  ))}
                </ul>
                <p className="text-sm">
                  The planner will check a route using these choices. Your saved
                  or started route still uses its existing review process.
                </p>
                {stale && (
                  <p role="status" className="text-sm">
                    Planner choices changed. Go back and review again.
                  </p>
                )}
                <button
                  className={button}
                  disabled={
                    busy || plannerBusy || !!stale || !preview?.changes.length
                  }
                  onClick={() =>
                    void run(
                      async () => {
                        if (
                          !snapshot ||
                          !readFeedbackSnapshot(snapshot, context.owner) ||
                          Date.now() - Date.parse(reviewed.checkedAt) > 120000
                        )
                          throw new Error(
                            "Refresh suggestions and review again; this review has expired.",
                          );
                        const fresh = await loadFeedback(context);
                        if (
                          feedbackEvidenceKey(fresh) !== reviewed.evidence ||
                          (await listFeedbackDrafts(context)).length
                        )
                          throw new Error(
                            "Your feedback or settings changed. Refresh and review the suggestions again.",
                          );
                        return fresh;
                      },
                      (fresh) => {
                        const current = latest.current;
                        if (
                          current.plannerBusy ||
                          current.ownerKey !== ownerKey ||
                          JSON.stringify(current.prefs) !== reviewed.prefs
                        ) {
                          setMessage(
                            "Your planner changed. Review the suggestions again.",
                          );
                          return;
                        }
                        const applied = applyPersonalPreferenceSuggestions(
                          current.prefs,
                          reviewOutingFeedback(fresh, current.prefs.sport),
                          reviewed.selected,
                        );
                        if (!applied.changes.length) {
                          setMessage("No preference changes remain to apply.");
                          return;
                        }
                        onApply(
                          applied.preferences,
                          applied.touchedFields,
                          reviewed.prefs,
                          ownerKey,
                        );
                        setReviewed(null);
                        setSelected([]);
                        setSnapshot(fresh);
                        setMessage(
                          "Reviewed preferences applied to this planner visit.",
                        );
                      },
                    )
                  }
                >
                  Apply reviewed visit preferences
                </button>
                <button
                  className={button}
                  disabled={busy}
                  onClick={() => setReviewed(null)}
                >
                  Back to suggestions
                </button>
              </div>
            ) : (
              <button
                className={button}
                disabled={busy || plannerBusy || !preview?.changes.length}
                onClick={() => {
                  if (!readFeedbackSnapshot(snapshot, context.owner)) {
                    setMessage("Refresh suggestions before reviewing them.");
                    return;
                  }
                  setReviewed({
                    evidence: feedbackEvidenceKey(snapshot),
                    prefs: JSON.stringify(prefs),
                    selected: [...selected],
                    checkedAt: snapshot.checkedAt,
                  });
                }}
              >
                Review selected suggestions
              </button>
            )}
          </>
        )}
        {message && (
          <p role="status" className="text-sm text-navy">
            {message}
          </p>
        )}
      </div>
    </details>
  );
}
